<template>
  <dashboard-section
      v-if="!isFinishedMatch || appointment"
      :bordered="$device.isMobileOrTablet"
      :collapsed="collapsed && !editMode"
      :show-shadow="false"
      :min-visible-content-height="minVisibleContentHeight">
    <div
        v-if="!$device.isMobile || appointment"
        slot="header"
        class="d-flex flex-1-1 w-100 ai-center jc-between">
      <span
          class="APPOINTMENT-TITLE"
          :class="{ 'PAST': isDimmedAppointment }"
          :selenium="`appointment-title-for-${appointmentPurpose.toLowerCase()}`">
        <span class="ellipsize">Customer Roof Installation </span>
      </span>
      <transition name="FADE" mode="out-in">
        <span v-if="$device.isNeitherMobileOrTablet && !editMode && appointment" class="d-flex">
          <a
              class="EXPORT-APPOINTMENT"
              @click.prevent="onAppointmentsExport"
              selenium="export-appointment">
            <vk-icon name="export" class="ICON" selenium="export-icon" />Export
          </a>
          <a
              v-if="!isFinishedMatch"
              class="EDIT-APPOINTMENT"
              @click.prevent="onAppointmentsEdit"
              selenium="edit-appointment">
            <vk-icon name="pencil" class="ICON" selenium="edit-icon" />Edit
          </a>
          <a
              class="DELETE-APPOINTMENT"
              @click.prevent="openDeleteConfirmationModal"
              selenium="delete-appointment">
            <vk-icon name="trash" class="ICON" selenium="delete-icon" />Delete
          </a>
        </span>
      </transition>
      <template v-if="$device.isMobileOrTablet">
        <slot name="collapse" />
      </template>
    </div>
    <template slot-scope="{ calculateCollapsedHeight }">
      <transition name="FADE" mode="out-in">
        <form
            v-if="!$device.isMobile && (appointment || editMode)"
            class="d-flex flex-1-1 fd-column m-t-1"
            @submit.prevent="onAppointmentsSubmit">
          <div class="d-flex text-semibold ai-center">
          </div>
          <masonry-columns class="APPOINTMENT-MASONRY-COLUMNS">
            <template slot-scope="{ calculateCellPositions }">
              <masonry-columns-cell :bordered="$device.isMobileOrTablet" class="WRAP GROW">
                <h6
                    class="h6 text-semibold m-0"
                    :class="{ 'PAST': isDimmedAppointment }"
                    selenium="appointment-location-title">
                  Location
                </h6>
                <transition
                    name="FADE"
                    mode="out-in"
                    @after-leave="() => { calculateCellPositions(); calculateCollapsedHeight(); }">
                  <location-input
                      v-if="editMode"
                      v-model="address"
                      class="d-flex fd-column p-t-1 p-b-2 p-b-3:d"
                      selenium="appointment-location-container"
                  />
                  <div v-else-if="appointment.address" class="d-flex fd-column ai-start">
                    <a
                        :href="mapsUrl"
                        :class="{ 'PAST': isDimmedAppointment }"
                        class="link x-white-space-pre-wrap"
                        rel="noopener noreferrer"
                        target="_blank">{{appointment.address | location(multiline=true)}}</a>
                    <p
                        class="text-md c-gray60 p-b-2"
                        :class="{ 'PAST': isDimmedAppointment }">
                      {{ appointment.address.address_line_2 }}
                    </p>
                    <a
                        :href="mapsUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="d-flex">
                      <img
                          alt="Map"
                          class="bd-gray30"
                          :class="{ 'PAST-OPACITY': isDimmedAppointment }"
                          :src="getMapsSrc({ scale: 3 })"
                          :srcset="`${getMapsSrc({ scale: 1 })} 1x, ${getMapsSrc({ scale: 2 })} 2x`"
                          @load="() => { calculateCellPositions(); calculateCollapsedHeight(); }"
                      />
                    </a>
                  </div>
                  <p
                      v-else
                      class="text-lg c-gray70"
                      :class="{ 'PAST': isDimmedAppointment }">
                    (not provided)
                  </p>
                </transition>
              </masonry-columns-cell>
              <transition
                  name="FADE"
                  mode="out-in"
                  @after-leave="() => { calculateCellPositions(); calculateCollapsedHeight(); }">
                <masonry-columns-cell v-if="editMode" :bordered="$device.isMobileOrTablet" class="MOVE">
                  <div class="d-flex ai-center jc-end">
                    <a
                        class="CANCEL-EDIT-APPOINTMENT"
                        @click.prevent="onAppointmentsCancel"
                        selenium="appointment-button-cancel">
                      Cancel
                    </a>
                    <button
                        :disabled="!hasAppointmentChanges || isFetching"
                        class="SUBMIT-APPOINTMENT"
                        selenium="appointment-button-submit"
                        :class="{ 'x-pulse': isFetching }">
                      <span v-if="!isFetching">Save</span>
                    </button>
                  </div>
                </masonry-columns-cell>
              </transition>
            </template>
          </masonry-columns>
          <template v-if="!editMode && $device.isNeitherMobileOrTablet">
            <slot name="collapse" />
          </template>
        </form>
        <div v-else-if="$device.isMobile && appointment" class="d-flex flex-1-1 fd-column WRAP">
          <div class="d-flex">
            <vk-icon
                name="simple-clock"
                class="MODAL-ICON"
                :class="{ 'PAST': isDimmedAppointment }" />
            <p class="text-semibold p-l-5 p-b-2" :class="{ 'PAST': isDimmedAppointment }">
              {{ new Date(appointment.start_dtm) | dateDuration(
                isOneAllDay ? appointment.end_dtm : appointment.end_dtm,
                new Date(),
                { weekday: true, timeOfDay: !isAllDay }
              ) }}
            </p>
          </div>
          <div class="d-flex">
            <vk-icon name="pin" class="MODAL-ICON" :class="{ 'PAST': isDimmedAppointment }" />
            <div
                v-if="appointment.address"
                class="d-flex fd-column text-semibold p-l-5"
                :class="{ 'PAST': isDimmedAppointment }">
              <a
                  :href="mapsUrl"
                  :class="{ 'PAST': isDimmedAppointment }"
                  class="link x-white-space-pre-wrap"
                  rel="noopener noreferrer"
                  target="_blank">{{appointment.address | location(multiline=true)}}</a>
              <p
                  class="text-md c-gray60 p-b-1"
                  :class="{ 'PAST': isDimmedAppointment }">
                {{ appointment.address.address_line_2 }}
              </p>
            </div>
            <p
                v-else
                class="text-lg c-gray70 p-l-5"
                :class="{ 'PAST': isDimmedAppointment }">
              (not provided)
            </p>
          </div>
          <a
              v-if="appointment.address"
              :href="mapsUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="d-flex jc-center">
            <img
                alt="Map"
                class="d-flex bd-gray30"
                :class="{ 'PAST-OPACITY': isDimmedAppointment }"
                :src="getMapsSrc({ scale: 3 })"
                :srcset="`${getMapsSrc({ scale: 1 })} 1x, ${getMapsSrc({ scale: 2 })} 2x`"
                @load="calculateCollapsedHeight"
            />
          </a>
        </div>
      </transition>
      <sliding-modal
          :is-open="$device.isMobile && editModeMobile"
          backdrop-class="bg-transparent"
          transition-name="SLIDE-RIGHT"
          @secondary="onAppointmentsCancel">
        <vk-modal-body slot="body" class="SHADOW">
          <form class="MODAL-FORM" @submit.prevent="onAppointmentsSubmit">
            <vk-modal-header class="bd-b-gray30">
              <div class="d-flex jc-center ai-center">
                <a class="MODAL-CANCEL-EDIT-APPOINTMENT" @click.prevent="onAppointmentsCancel">
                  Cancel
                </a>
                <span class="d-flex text-bold">Customer Address for Roofing</span>
                <button
                    class="MODAL-SUBMIT-APPOINTMENT"
                    :class="{ 'DISABLED': !hasAppointmentChanges || isFetching }">
                  Save
                </button>
              </div>
            </vk-modal-header>
            <vk-modal-content class="d-flex fd-column p-0">
              <dashboard-section :bordered="$device.isMobileOrTablet">
                <location-input
                    inline
                    v-model="address"
                    class="MODAL-SECTION"
                />
              </dashboard-section>
              <dashboard-section />
            </vk-modal-content>
          </form>
        </vk-modal-body>
      </sliding-modal>
      <confirmation-modal
          :is-open="isDeleteConfirmationModalOpened"
          @secondary="closeDeleteConfirmationModal">
        Are you sure you want to delete this appointment?
        <button
            slot="primary"
            class="DELETE-APPOINTMENT-BUTTON"
            @click.prevent="onAppointmentsDelete">
          Delete
        </button>
      </confirmation-modal>
      <confirmation-modal
          :is-open="isEmptyAddressModalOpened"
          @secondary="closeEmptyAddressModal">
        Please enter a customer address before saving.
        <button
            slot="primary"
            class="DELETE-APPOINTMENT-BUTTON"
            @click.prevent="closeEmptyAddressModal">
          OK
        </button>
      </confirmation-modal>
    </template>
    <transition
        v-if="$device.isMobileOrTablet && !editMode && appointment"
        slot="footer"
        name="FADE"
        mode="out-in">
      <span class="d-flex flex-1-1 jc-between p-y-2">
        <a
            class="DELETE-APPOINTMENT"
            @click.prevent="openDeleteConfirmationModal">
          <vk-icon name="trash" class="ICON" />Delete
        </a>
        <a class="EXPORT-APPOINTMENT" @click.prevent="onAppointmentsExport">
          <vk-icon name="export" class="ICON" />Export
        </a>
        <a v-if="!isFinishedMatch" class="EDIT-APPOINTMENT" @click.prevent="onAppointmentsEdit">
          <vk-icon name="pencil" class="ICON" />Edit
        </a>
      </span>
    </transition>
  </dashboard-section>
</template>

<script>
import _isEqual from 'lodash/isEqual';
import {
  isBefore,
  isEqual,
  isSameDay,
  differenceInHours,
  addDays,
  addHours,
  addMinutes,
  setHours,
  setMinutes,
} from 'date-fns';

import { mapGetters } from 'vuex';
import { PURPOSES, PURPOSES_MAP } from '@/constants/appointments';
import jobParams from '@/mixins/job-params';
import job from '@/mixins/job';
import subtask from '@/mixins/job/subtask';
import appointments from '@/mixins/job/appointments';
import fullName from '@/filters/full-name';
import location from '@/filters/location';
import DateTimeInput from '@/views/Dashboard/components/DateTimeInput';
import LocationInput from '@/views/Dashboard/components/LocationInput';
import SlidingModal from '@/views/Dashboard/components/SlidingModal';
import ConfirmationModal from '@/views/Dashboard/components/ConfirmationModal';
import DashboardSection from '@/views/Dashboard/components/DashboardSection';
import MasonryColumns from '@/views/Dashboard/components/MasonryColumns';
import MasonryColumnsCell from '@/views/Dashboard/components/MasonryColumns/MasonryColumnsCell';

import PurposeInput from './PurposeInput';

const HOURS_IN_DAY = 24;

export default {
  name: 'appointment-section',
  components: {
    DateTimeInput,
    LocationInput,
    SlidingModal,
    ConfirmationModal,
    DashboardSection,
    MasonryColumns,
    MasonryColumnsCell,
    PurposeInput,
  },
  mixins: [
    jobParams,
    job,
    subtask,
    appointments,
  ],
  props: {
    id: [String, Number],
    collapsed: Boolean,
  },
  data () {
    const today = new Date();

    today.setSeconds(0, 0);

    return {
      today,
      editMode: false,
      editModeMobile: false,
      isDeleteConfirmationModalOpened: false,
      isEmptyAddressModalOpened: false,
      startDateTime: addHours(today, 1),
      endDateTime: addHours(today, 2),
      isAllDay: false,
      address: undefined,
      purpose: {
        purpose: PURPOSES_MAP[PURPOSES.call].value,
        otherPurpose: '',
      },
    };
  },
  computed: {
    ...mapGetters('account/pro/info', [
      'serviceProviderId',
    ]),
    // Used by appointment mixin.
    appointmentId () {
      return this.id;
    },
    // Used by subtask mixin.
    subtaskId () {
      return this.job.subtask;
    },
    isFetching () {
      const { meta, status } = this.currentJobAppointmentsMeta;

      if (!meta) {
        return false;
      }

      const isOwnFetchingStatus = this.appointmentId
        ? meta.appointmentId === this.appointmentId
        : true;

      return isOwnFetchingStatus && status === this.$options.REQUEST_LIFECYCLE.FETCHING;
    },
    isOneAllDay () {
      const diff = differenceInHours(this.endDateTime, this.startDateTime);

      return this.isAllDay && diff > HOURS_IN_DAY;
    },
    isDimmedAppointment () {
      return !this.editMode && isBefore(new Date(this.endDateTime), Date.now());
    },
    mapsLocation () {
      return location(this.address).replace(/\s/g, '+');
    },
    mapsUrl () {
      return `https://www.google.com/maps/search/?api=1&query=${this.mapsLocation}`;
    },
    hasAppointmentChanges () {
      return (
        !this.appointment
        || !isEqual(this.startDateTime, new Date(this.appointment.start_dtm))
        || !isEqual(this.endDateTime, new Date(this.appointment.end_dtm))
        || !_isEqual(this.appointment.address || undefined, this.address)
        || !_isEqual(this.appointment.purpose, this.purpose.purpose)
        || !_isEqual(this.appointment.purpose_description || '', this.purpose.otherPurpose)
      );
    },
    minVisibleContentHeight () {
      if (this.$device.isWide) {
        return '131px';
      }

      if (this.$device.isDesktop) {
        return '207px';
      }

      if (this.$device.isTablet) {
        return '216px';
      }

      return '48px';
    },
    appointmentPurpose () {
      return this.purpose.otherPurpose
        ? this.purpose.otherPurpose
        : PURPOSES_MAP[this.purpose.purpose].short;
    },
  },
  watch: {
    appointment () {
      this.reconcile();
    },
    startDateTime () {
      this.syncDateTimes(60);
    },
    endDateTime () {
      this.syncDateTimes(30);
      this.setIsAllDay();
    },
  },
  mounted () {
    this.reconcile();
  },
  methods: {
    setIsAllDay () {
      const hours = this.startDateTime.getHours();
      const minutes = this.startDateTime.getMinutes();
      const diff = differenceInHours(this.endDateTime, this.startDateTime);

      this.isAllDay = !hours && !minutes && diff >= HOURS_IN_DAY && !(diff % HOURS_IN_DAY);
    },
    syncDateTimes (gapInMinutes) {
      if (this.isAllDay && !isBefore(new Date(this.startDateTime), new Date(this.endDateTime))) {
        this.endDateTime = addDays(this.startDateTime, 1);
      } else if (
        (this.editMode || this.editModeMobile)
        && (isBefore(new Date(this.endDateTime), new Date(this.startDateTime))
          || isEqual(this.endDateTime, this.startDateTime))
      ) {
        this.endDateTime = addMinutes(this.startDateTime, gapInMinutes);
      }
    },
    startDatePredictor (dateComponent, date) {
      return isBefore(date, this.today);
    },
    endDatePredictor (dateComponent, date) {
      const compareToDateTime = addMinutes(this.startDateTime, 30);

      if (dateComponent === 'date') {
        return (
          !isSameDay(date, compareToDateTime)
          && isBefore(date, compareToDateTime)
        );
      }

      return isBefore(date, compareToDateTime);
    },
    getMapsSrc ({ scale }) {
      return `https://maps.googleapis.com/maps/api/staticmap?center=${this.mapsLocation}&zoom=15&scale=${scale}&size=320x160&key=${window.$mapsKey}`;
    },
    // Since this component is used to create or update appointment - we are storing local appointment representation and reconciling it after each appointment update.
    // When you are creating appointment there is no data to reconcile with so we are setting default values. In most cases local appointment representation should be used.
    reconcile ({ reset } = {}) {
      if (!this.appointment || reset) {
        // For address-only mode, use fixed defaults as per requirements:
        // Date: 1/1/2020, Time: 12pm, Type: Other
        this.startDateTime = new Date('2020-01-01T12:00:00');
        this.endDateTime = new Date('2020-01-01T12:00:00');
        this.address = undefined;
        this.purpose = {
          purpose: PURPOSES_MAP[PURPOSES.other].value,
          otherPurpose: 'roofing_installation',
        };

        this.onAppointmentsEdit();
      } else {
        this.startDateTime = new Date(this.appointment.start_dtm);
        this.endDateTime = new Date(this.appointment.end_dtm);
        this.address = this.appointment.address || undefined;
        this.purpose = {
          purpose: PURPOSES_MAP[this.appointment.purpose]
            ? PURPOSES_MAP[this.appointment.purpose].value
            : PURPOSES.other,
          otherPurpose: this.appointment.purpose_description,
        };
      }
    },
    onAppointmentsEdit () {
      if (this.$device.isMobile) {
        this.editModeMobile = true;
      } else {
        this.editMode = true;
      }
    },
    async onAppointmentsExport () {
      const { first_name, last_name } = this.job.location;
      const customersFullName = fullName(first_name, last_name);
      const { data, headers } = await this.exportAppointments({
        appointmentId: this.appointment.appointment_id,
        serviceProviderId: this.appointment.service_provider_id,
      });
      const blob = new Blob([data], { type: headers['Content-Type'] });
      const link = global.document.createElement('a');
      link.href = global.window.URL.createObjectURL(blob);
      link.download = `${customersFullName} - ${this.job.service_name}.ics`.replace(/\s/g, '_');

      link.click();
    },
    onAppointmentsCancel () {
      this.reconcile();

      this.editMode = false;
      this.editModeMobile = false;

      this.$emit('cancel');
    },
    async onAppointmentsSubmit () {
      // Validate address is not empty
      if (!this.address || !this.address.address_line_1) {
        this.isEmptyAddressModalOpened = true;
        return;
      }

      const data = await this.createOrUpdateAppointments({
        jobId: this.job.id,
        skillsProfile: this.match.skills_profile,
        appointmentId: this.appointment ? this.appointment.appointment_id : undefined,
        params: {
          order_hash: this.job.order_hash,
          service_provider_id: this.serviceProviderId,
          start_dtm: this.startDateTime,
          end_dtm: this.endDateTime,
          address: this.address,
          purpose: this.purpose.purpose,
          purpose_description: this.purpose.otherPurpose || 'roofing_installation',
        },
      });
      this.editMode = false;
      this.editModeMobile = false;
      this.$emit('change', this.appointment || data);

      if (!this.appointment) {
        this.trackUpdates(this.$events.APPOINTMENT_CREATED);
      }
    },
    async onAppointmentsDelete () {
      await this.deleteAppointments({
        order_hash: this.job.order_hash,
        service_provider_id: this.serviceProviderId,
        appointmentId: this.appointment.appointment_id,
      });

      this.closeDeleteConfirmationModal();

      this.trackUpdates(this.$events.APPOINTMENT_DELETED);
    },
    trackUpdates (event) {
      this.$events.$emit(event, {
        jobId: this.job.id,
        allDay: this.isAllDay,
        startDate: this.startDateTime.toISOString(),
        endDate: this.endDateTime.toISOString(),
        address: location(this.address),
        type: this.purpose.purpose,
        message: this.purpose.otherPurpose,
      });
    },
    openDeleteConfirmationModal () {
      this.isDeleteConfirmationModalOpened = true;
    },
    closeDeleteConfirmationModal () {
      this.isDeleteConfirmationModalOpened = false;
    },
    closeEmptyAddressModal () {
      this.isEmptyAddressModalOpened = false;
    },
    onAllDayClick () {
      if (this.isAllDay) {
        this.reconcile({ reset: true });
      } else {
        this.startDateTime = setHours(setMinutes(this.startDateTime, 0), 0);
        this.endDateTime = addDays(this.startDateTime, 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  @import "~@/views/Dashboard/constants";

  .APPOINTMENT-TITLE {
    @include text-large;
    display: flex;
    min-width: 0;
    align-items: center;
    font-weight: 700;
  }

  .ICON {
    @include h4;
    flex: 0 0 auto;
    margin-right: $unit/2;
  }

  .TOGGLE:before {
    @include square(28px);
    margin: 0 0 0 0.25*$unit;
  }

  input:checked + .TOGGLE {
    background-color: $color-green;

    &:before {
      margin: 0 0 0 0.75*$unit;
    }
  }

  .APPOINTMENT-MASONRY-COLUMNS {
    margin-top: 2*$unit;

    .GROW {
      @include responsive(grid-column-end, auto, $lg: span 2);
    }

    .MOVE {
      @include responsive(grid-column, auto, $lg: 2);
    }
  }

  .EDIT-APPOINTMENT,
  .DELETE-APPOINTMENT,
  .EXPORT-APPOINTMENT {
    @include link-primary;
  }

  .EDIT-APPOINTMENT,
  .EXPORT-APPOINTMENT,
  .CANCEL-EDIT-APPOINTMENT {
    @include margin-right(0, $md: 3);
  }

  .EDIT-APPOINTMENT,
  .DELETE-APPOINTMENT,
  .EXPORT-APPOINTMENT,
  .CANCEL-EDIT-APPOINTMENT {
    @include text-large;
    @include hover(text-decoration, none, none);
    display: flex;

    @include desktop {
      @include text-medium;
    }
  }

  .EDIT-APPOINTMENT,
  .DELETE-APPOINTMENT,
  .EXPORT-APPOINTMENT {
    align-items: center;
  }

  .EDIT-APPOINTMENT,
  .DELETE-APPOINTMENT,
  .EXPORT-APPOINTMENT {
    .ICON {
      @include desktop {
        @include h5;
      }
    }
  }

  .CANCEL-EDIT-APPOINTMENT {
    @include link-black;
    color: $color-gray80;
    margin-right: 3*$unit;
  }

  .SUBMIT-APPOINTMENT {
    @include button-primary;
    @include button-small;
    width: 12*$unit;
    font-weight: 600;
  }

  .DELETE-APPOINTMENT-BUTTON {
    @include button-primary;
    @include button-medium;
    display: flex;
    flex: 1 1 auto;
    margin-left: 1.5*$unit;
    font-weight: 600;
  }

  .PLACEHOLDER {
    @include rect(250px, 16px);
    margin: $unit/2 0;
  }

  .MODAL-FORM {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
  }

  .MODAL-CANCEL-EDIT-APPOINTMENT,
  .MODAL-SUBMIT-APPOINTMENT {
    @include absolute;
  }

  .MODAL-CANCEL-EDIT-APPOINTMENT {
    @include link-black;
    @include text-large;
    left: 2*$unit;
  }

  .MODAL-SUBMIT-APPOINTMENT {
    @include link-primary;
    @include text-large;
    right: 2*$unit;
    padding: 0;
    background: none;
    border: none;
    appearance: none;
    will-change: color;
    transition: color $transition-half-duration;

    &:focus {
      outline: none;
    }

    &.DISABLED {
      @include hover(color, $color-gray40, $color-gray40);
      pointer-events: none;
    }
  }

  .MODAL-SECTION {
    display: flex;
    flex: 1 1;
    flex-direction: column;
    margin: -2*$unit;
  }

  .MODAL-SECTION-ROW {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5*$unit 2*$unit;

    &:not(:last-of-type) {
      border-bottom: 1px solid $color-gray30;
    }
  }

  .MODAL-PURPOSE-SECTION {
    padding: 1.5*$unit 2*$unit;
  }

  .MODAL-ICON {
    @include absolute;
    @include h4;
    color: $color-gray40;
  }

  .SHADOW {
    @include responsive(box-shadow, 0 0 10px rgba(black, 0.2), $md: none);
  }

  .WRAP {
    overflow-wrap: break-word;
    word-break: break-word;
  }

  .PAST {
    color: inherit !important;
  }

  .PAST-OPACITY {
    opacity: 1;
  }
</style>
